import React from 'react';
import { motion, MotionProps, AnimatePresence as FramerAnimatePresence } from 'framer-motion';

const smooth = [0.22, 1, 0.36, 1] as const;

export const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
};
export const slideUp = {
    hidden: { opacity: 0, y: 36 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: smooth } },
};
export const staggerContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
};
export const staggerItem = {
    hidden: { opacity: 0, y: 18 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.38, ease: smooth } },
};

type MotionTag = 'div' | 'button' | 'li' | 'section' | 'header' | 'label' | 'main' | 'ol';

type MotionComponentProps<T extends MotionTag = MotionTag> = {
    children?: React.ReactNode;
    className?: string;
    tag: T;
} & MotionProps &
    Omit<React.ComponentPropsWithoutRef<T>, keyof MotionProps | 'children' | 'className'>;

const useCanUseFramer = () => {
    const [mounted, setMounted] = React.useState(false);

    React.useEffect(() => {
        setMounted(true);
    }, []);

    return mounted;
};

const Motion = <T extends MotionTag>({ children, tag, className, ...framerProps }: MotionComponentProps<T>) => {
    const canUseFramer = useCanUseFramer();

    // SSR
    if (!canUseFramer) {
        return React.createElement(tag, { className }, children);
    }

    const Component = motion[tag] as React.ComponentType<
        MotionProps & { children?: React.ReactNode; className?: string }
    >;

    return React.createElement(Component, { ...(framerProps as MotionProps), className }, children);
};

export const AnimatePresence = ({ children }: { children: React.ReactNode }) => {
    const canUseFramer = useCanUseFramer();

    // SSR
    if (!canUseFramer) {
        return children;
    }

    return <FramerAnimatePresence>{children}</FramerAnimatePresence>;
};

export default Motion;
